import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Paintbrush } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api } from "@/lib/api";

const PRESETS = ["#f97316", "#ef4444", "#eab308", "#22c55e", "#14b8a6", "#3b82f6", "#a855f7", "#ec4899"];

function hexToHsl(hex) {
  const n = parseInt(hex.slice(1), 16);
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => v / 255);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  let h = 0;
  const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
  if (d) {
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
  }
  return `${Math.round(((h * 60) + 360) % 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

function applyAccent(hex) {
  const root = document.documentElement.style;
  if (!hex) {
    ["--primary", "--ring", "--ls-active"].forEach((v) => root.removeProperty(v));
    return;
  }
  root.setProperty("--primary", hexToHsl(hex));
  root.setProperty("--ring", hexToHsl(hex));
  root.setProperty("--ls-active", hex);
}

export function DashboardColor({ t }) {
  const [accent, setAccent] = useState(null);

  useEffect(() => {
    api.get("/admin/prefs").then((r) => setAccent(r.data.accent)).catch(() => {});
    return () => applyAccent(null);
  }, []);
  useEffect(() => {
    if (accent) applyAccent(accent);
  }, [accent]);

  const save = async (hex) => {
    setAccent(hex);
    try {
      await api.put("/admin/prefs", { accent: hex });
    } catch {
      toast.error(t("toast.saveFailed"));
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" aria-label={t("dash.color")} data-testid="dashboard-color-btn"><Paintbrush className="h-4 w-4 text-primary" /></Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 space-y-3" data-testid="dashboard-color-popover">
        <div>
          <div className="text-sm font-semibold">{t("dash.color")}</div>
          <p className="text-xs text-muted-foreground">{t("dash.colorHint")}</p>
        </div>
        <div className="grid grid-cols-8 gap-1.5">
          {PRESETS.map((c) => (
            <button key={c} type="button" onClick={() => save(c)} aria-label={c} data-testid={`dashboard-color-${c.slice(1)}`}
              className="grid aspect-square place-items-center rounded-full ring-offset-2 ring-offset-popover transition-transform hover:scale-110" style={{ background: c }}>
              {accent === c && <Check className="h-3.5 w-3.5 text-white" />}
            </button>
          ))}
        </div>
        <label className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
          {t("dash.custom")}
          <input type="color" value={accent || "#f97316"} onChange={(e) => setAccent(e.target.value)} onBlur={(e) => save(e.target.value)} className="h-8 w-12 cursor-pointer rounded border bg-transparent p-0.5" data-testid="dashboard-color-custom" />
        </label>
      </PopoverContent>
    </Popover>
  );
}
